import { Badge, Card, CardContent, CardHeader, CardTitle, Label, Switch } from '@folio/components';
import { cn } from '@folio/lib';
import { getDocumentCategoryLabel } from '@folio/utils/getDocumentCategoryLabel';
import { useExpectedUploadedDocuments } from '@folio/utils/useExpectedUploadedDocuments';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useQuery } from '@tanstack/react-query';
import {
  keyValueFieldsToObject,
  policyControllerListPolicyDocumentsQueryOptions,
  policyDocumentControllerGetPolicyDocumentConformityQueryOptions,
} from 'korint-sdk';
import { useFeatureFlag } from 'korint-sdk/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type CategoryConformityStatus = 'accepted' | 'rejected' | 'pending' | 'missing';

const STATUS_CONFIG: Record<CategoryConformityStatus, { pillClassName: string; labelKey: string }> =
  {
    accepted: {
      pillClassName: 'text-emerald-700 bg-emerald-50',
      labelKey: 'documentsListsPolicy.conformity.acceptedLegacy',
    },
    rejected: {
      pillClassName: 'text-destructive bg-destructive/10',
      labelKey: 'documentsListsPolicy.conformity.rejected',
    },
    pending: {
      pillClassName: 'text-amber-700 bg-amber-50',
      labelKey: 'documentsListsPolicy.conformity.pending',
    },
    missing: {
      pillClassName: 'text-muted-foreground bg-muted',
      labelKey: 'documentConformityChecklist.status.missing',
    },
  };

export interface DocumentConformityChecklistModuleProps {
  /** Rendered inline in the header; falls back to a standalone switch when there's no
   * checklist to merge into. */
  validateSwitch?: {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    disabled?: boolean;
    label: string;
  };
}

/** Mirrors the backend's isDocumentConform rule: required category present + accepted. */
export function DocumentConformityChecklistModule({
  validateSwitch,
}: DocumentConformityChecklistModuleProps = {}) {
  const { t } = useTranslation();
  const { policy, workingBranch } = usePolicyOnDashboard();

  const { data: expectedDocuments } = useExpectedUploadedDocuments({
    productId: policy.productId,
    policyId: policy.id,
    branchId: workingBranch?.id,
  });

  const requiredDocumentConfigs = expectedDocuments?.filter(document => document.required);

  const conformityBranchId = workingBranch?.id ?? 'BASE';
  const documentConformityFlagEnabled = useFeatureFlag(
    'feat_document_conformity',
    policy.productId,
  );

  const { data: conformityData, isSuccess: isDocumentReviewEnabled } = useQuery({
    ...policyDocumentControllerGetPolicyDocumentConformityQueryOptions(policy.id, {
      branchId: conformityBranchId,
    }),
    retry: false,
    enabled: documentConformityFlagEnabled,
  });

  const { data: policyDocuments } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policy.id, {
      branchId: workingBranch?.id,
    }),
    select: data =>
      data.data
        ?.map(document => ({ ...document, metadata: keyValueFieldsToObject(document.metadata) }))
        .filter(document => document.metadata.archived !== 'true'),
  });

  const decisionByDocumentId = useMemo(
    () =>
      new Map(
        (conformityData?.conformities ?? [])
          .filter(conformity => conformity.status === 'ACTIVE')
          .map(conformity => [conformity.documentId, conformity.decision]),
      ),
    [conformityData],
  );

  const documentsByCategory = useMemo(() => {
    const map = new Map<string, NonNullable<typeof policyDocuments>>();
    for (const document of policyDocuments ?? []) {
      const category = document.category;
      if (typeof category !== 'string') continue;
      const documentsInCategory = map.get(category) ?? [];
      documentsInCategory.push(document);
      map.set(category, documentsInCategory);
    }
    return map;
  }, [policyDocuments]);

  const getCategoryStatus = (category: string): CategoryConformityStatus => {
    const documentsInCategory = documentsByCategory.get(category);
    if (!documentsInCategory?.length) return 'missing';

    const decisions = documentsInCategory.map(document => decisionByDocumentId.get(document.id));
    // Mirrors the backend's acceptedCategories rule: one accepted document is enough,
    // even if the category also holds an earlier rejected document.
    if (decisions.some(decision => decision === 'ACCEPTED')) return 'accepted';
    if (decisions.some(decision => decision === 'REJECTED')) return 'rejected';
    return 'pending';
  };

  if (!isDocumentReviewEnabled || !requiredDocumentConfigs?.length) {
    // No checklist to merge the switch into (product has no document review, or no
    // required categories) — fall back to the switch on its own so products that only
    // ever had this manual toggle keep it.
    if (!validateSwitch) return null;
    return (
      <Card>
        <CardContent className="flex items-center gap-3 pt-6">
          <Switch
            checked={validateSwitch.checked}
            onCheckedChange={validateSwitch.onCheckedChange}
            disabled={validateSwitch.disabled}
            id="document-validation"
          />
          <Label htmlFor="document-validation">{validateSwitch.label}</Label>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle>{t('documentConformityChecklist.title')}</CardTitle>
        {validateSwitch && (
          <Group gap={2.5} className="flex-shrink-0">
            <Switch
              checked={validateSwitch.checked}
              onCheckedChange={validateSwitch.onCheckedChange}
              disabled={validateSwitch.disabled}
              id="document-validation"
            />
            <Label htmlFor="document-validation" className="text-sm font-normal whitespace-nowrap">
              {validateSwitch.label}
            </Label>
          </Group>
        )}
      </CardHeader>
      <Stack asChild gap={1}>
        <CardContent>
          {requiredDocumentConfigs.map(({ category }) => {
            const status = getCategoryStatus(category);
            const { pillClassName, labelKey } = STATUS_CONFIG[status];
            return (
              <Group key={category} gap={3} className="py-2.5 border-b border-border last:border-0">
                <Text inline size="sm" className="flex-1">
                  {getDocumentCategoryLabel(category, requiredDocumentConfigs, t)}
                </Text>
                <Badge
                  variant="outline"
                  className={cn('border-transparent flex-shrink-0', pillClassName)}
                >
                  {t(labelKey)}
                </Badge>
              </Group>
            );
          })}
        </CardContent>
      </Stack>
    </Card>
  );
}
